import { useEffect, useRef, useState } from "react";
import { Input } from "react-daisyui";
import { useTranslation } from "react-i18next";
import { HiMicrophone, HiSearch } from "react-icons/hi";

interface SearchBarProps {
  onChange: (val: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  suggestions?: string[];
  value: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onend: (() => void) | null;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  start(): void;
  stop(): void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognition;
}

// Type definitions for Web Speech API
interface SpeechRecognitionEvent {
  results: {
    [key: number]: {
      [key: number]: {
        transcript: string;
      };
    };
  };
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

export default function SearchBar({
  onChange,
  onSubmit,
  suggestions = [],
  value,
}: SearchBarProps) {
  const { t } = useTranslation();
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<null | SpeechRecognition>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Setup voice recognition (Web Speech API)
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = "en-US";

      recognitionRef.current.onresult = (event: SpeechRecognitionEvent) => {
        const transcript = event.results[0][0].transcript;
        onChange(transcript);
        setIsListening(false);
        inputRef.current?.focus();
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, [onChange]);

  const handleVoiceClick = () => {
    if (!recognitionRef.current) return;
    if (isListening) {
      recognitionRef.current.stop();
    } else {
      setIsListening(true);
      recognitionRef.current.start();
    }
  };

  return (
    <div className="relative w-full max-w-xl">
      <form
        className="flex items-center overflow-hidden rounded-lg border border-base-300 bg-base-100 ring-primary focus-within:ring"
        onSubmit={onSubmit}
      >
        <Input
          autoComplete="off"
          className="input w-full border-none focus:outline-none focus:ring-0"
          id="search"
          onChange={(e) => onChange(e.target.value)}
          placeholder={t(
            "search.placeholder",
            "Search dashboard, staff, projects...",
          )}
          ref={inputRef}
          type="text"
          value={value}
        />
        <button
          aria-label={t("search.voice", "Voice search")}
          className="p-2 hover:text-primary"
          onClick={handleVoiceClick}
          type="button"
        >
          <HiMicrophone
            className={`h-5 w-5 ${isListening ? "animate-pulse text-red-500" : ""}`}
          />
        </button>
        <button
          aria-label={t("search.submit", "Submit search")}
          className="p-2 hover:text-primary"
          type="submit"
        >
          <HiSearch className="h-5 w-5" />
        </button>
      </form>

      {/* Suggestions */}
      {value && suggestions.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full rounded-md border border-base-200 bg-base-100 shadow-lg">
          {suggestions
            .filter((s) => s.toLowerCase().includes(value.toLowerCase()))
            .map((s, i) => (
              <li
                className="cursor-pointer px-4 py-2 hover:bg-base-200"
                key={i}
                onClick={() => onChange(s)}
              >
                {s}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
